import React, { useMemo } from 'react';
import './metricAttainment.scss';

interface MetricAttainmentProps {
    data: any[];
}

const LEVEL_COLOR: Record<number, string> = {
    1: '#E23636', // Below Meets
    2: '#2F80ED', // Meets
    3: '#2AA154', // Exceeds
};

const RING_RADIUS = 32;
const RING_STROKE = 7;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const OMITTED_COLOR = '#B7BDC3';

interface DisplayMetric {
    metricId: number;
    metricName: string;
    earnedRate: number;
    level: number;
    isOmitted: boolean;
}
const TOTAL_ATTAINMENT_LEVELS = 3;

function getRingColor(eligible: boolean, allExceeds: boolean): string {
    if (!eligible) return '#F2A900';
    if (allExceeds) return '#2AA154';
    return '#1A5AA5';
}

const MetricRateBar: React.FC<{ metric: DisplayMetric }> = ({ metric }) => {
    const color = metric.isOmitted ? OMITTED_COLOR : LEVEL_COLOR[metric.level] ?? '#9AA5AB';
    const fillPct = metric.isOmitted
        ? 100
        : Math.min(Math.max((metric.level / TOTAL_ATTAINMENT_LEVELS) * 100, 0), 100);

    return (
        <div className="attainment-dashboard__metric">
            <span className="attainment-dashboard__metric-name">{metric.metricName}</span>
            <div className="attainment-dashboard__bar-track">
                <div
                    className="attainment-dashboard__bar-fill"
                    style={{ width: `${fillPct}%`, background: color }}
                />
            </div>
            <span className="attainment-dashboard__metric-current" style={{ color }}>
                {metric.isOmitted ? 'Omitted Metric' : `Current: ${metric.earnedRate.toFixed(2)}%`}
            </span>
        </div>
    );
};

export const MetricAttainment: React.FC<MetricAttainmentProps> = ({ data }) => {
    const { metrics, totalMetricsAttained, totalMetricsTargeted, eligible, allExceeds } = useMemo(() => {
        const rows = data ?? [];

        const displayMetrics: DisplayMetric[] = rows.map((row) => ({
            metricId: row.metric_id,
            metricName: row.label,
            earnedRate: row.metric_payout_percentage ?? 0,
            level: row.metric_attainment_level_id ?? 1,
            isOmitted: row.metric_omitted,
        }));
        const first = rows[0];
        const attained = first?.total_metrics_attained ?? 0;
        const payoutMetrics = rows.filter((r) => r.is_payout_metric);
        const targeted = first?.total_metrics_targeted ?? payoutMetrics.length;        
        const isEligible =
            payoutMetrics.length > 0 &&
            !payoutMetrics.some((r) => (r.metric_attainment_level_id ?? 1) === 1);
        const isAllExceeds =
            payoutMetrics.length > 0 &&
            payoutMetrics.every((r) => (r.metric_attainment_level_id ?? 1) === 3);

        return {
            metrics: displayMetrics,
            totalMetricsAttained: attained,
            totalMetricsTargeted: targeted,
            eligible: isEligible,
            allExceeds: isAllExceeds,
        };
    }, [data]);

    const remaining = Math.max(totalMetricsTargeted - totalMetricsAttained, 0);
    const ringFraction = totalMetricsTargeted > 0 ? totalMetricsAttained / totalMetricsTargeted : 0;
    const ringOffset = RING_CIRCUMFERENCE * (1 - ringFraction);
    const ringColor = getRingColor(eligible, allExceeds);
    const remainingWord = remaining === 1 ? 'Metric' : 'Metrics';

    let messageContent: React.ReactNode;
    if (!eligible) {
        messageContent = (
            <>
                You are {remaining}{' '}
                <span className="attainment-dashboard__message-highlight" style={{ color: ringColor }}>
                    {remainingWord}
                </span>{' '}
                away. Remember that you need to{' '}
                <span className="attainment-dashboard__message-highlight" style={{ color: ringColor }}>
                    Meet all {totalMetricsTargeted} Metrics
                </span>{' '}
                to qualify for payout.
            </>
        );
    } else if (allExceeds) {
        messageContent = (
            <>
                Congratulations! You have hit the{' '}
                <span className="attainment-dashboard__message-highlight" style={{ color: ringColor }}>
                    top target
                </span>{' '}
                on all metrics.
            </>
        );
    } else {
        messageContent = (
            <>
                You have hit the{' '}
                <span className="attainment-dashboard__message-highlight" style={{ color: ringColor }}>
                    &quot;Meets&quot;
                </span>{' '}
                target on all {totalMetricsTargeted} metrics. You can still hit{' '}
                <span className="attainment-dashboard__message-highlight" style={{ color: ringColor }}>
                    &quot;Exceeds&quot;
                </span>{' '}
                targets to improve payout.
            </>
        );
    }

    return (
        <div className="attainment-dashboard">
            <h4 className="attainment-dashboard__title">Attainment</h4>

            <div className="attainment-dashboard__body">
                <div className="attainment-dashboard__metrics">
                    {metrics.map((metric) => (
                        <MetricRateBar key={metric.metricId} metric={metric} />
                    ))}
                </div>

                <div className="attainment-dashboard__divider" />

                <div className="attainment-dashboard__summary">
                    <svg
                        className="attainment-dashboard__ring"
                        viewBox="0 0 80 80"
                        role="img"
                        aria-label={`${totalMetricsAttained} of ${totalMetricsTargeted} metrics attained`}
                    >
                        <circle
                            cx={40}
                            cy={40}
                            r={RING_RADIUS}
                            className="attainment-dashboard__ring-track"
                            strokeWidth={RING_STROKE}
                            fill="none"
                        />
                        <circle
                            cx={40}
                            cy={40}
                            r={RING_RADIUS}
                            stroke={ringColor}
                            strokeWidth={RING_STROKE}
                            strokeLinecap="round"
                            fill="none"
                            strokeDasharray={RING_CIRCUMFERENCE}
                            strokeDashoffset={ringOffset}
                            transform="rotate(-90 40 40)"
                        />
                        <text x="40" y="46" textAnchor="middle" className="attainment-dashboard__ring-text" style={{ fill: ringColor }}>
                            {totalMetricsAttained}/{totalMetricsTargeted}
                        </text>
                    </svg>

                    <p className="attainment-dashboard__message">{messageContent}</p>
                </div>
            </div>
        </div>
    );
};

export default MetricAttainment;